import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import styled from "styled-components";
import { authType } from "../../../pages/Login";
import CalendarLineBtn from "../../Button/CalendarLineBtn";
import ConfirmBtn from "../../Button/ConfirmBtn";
import LineBtn from "../../Button/LineBtn";
import Flex from "../../DefaultStyles/Flex";
import DropDown, { OptionType } from "../../Filters/DropDown";
import Search from "../../Filters/Search";
import CalendarModal from "../../Modal/CalendarModal";
import ToolTip from "../../Modal/ToolTip";
import { CalendarLine } from "../Party/ChatInfo/ChatSearch/RecentChat";
import { PartyInfoFilterContainer } from "../Party/PartyInfo/PartyInfoFilter";
import { showIpLogState } from "../Users/UserDetail/UserDetailHeader";

interface UserLogFilterProps {
  filter: {
    type: string;
    typeLabel: string;
    searchValue: string;
    searchType: string;
    startDate: string;
    endDate: string;
    isDateClick: boolean;
  };
  setFilter: any;
  setPage: Dispatch<SetStateAction<number>>;
}

export default function UserLogFilter({
  filter,
  setFilter,
  setPage,
}: UserLogFilterProps) {
  const auth = useRecoilValue(authType);
  const [showIpLog, setShowIpLog] = useRecoilState(showIpLogState);

  const [typeDropdownValue, setTypeDropdownValue] = useState<OptionType>({
    value: "",
    label: "로그 타입 선택",
  });
  const [searchType, setSearchType] = useState<OptionType>({
    value: "",
    label: "선택",
  });
  const [searchValue, setSearchValue] = useState<string | undefined>("");
  const [searchDefaultValue, setSearchDefaultValue] = useState<
    string | undefined
  >("");

  const [startDate, setStartDate] = useState("날짜 선택");
  const [startDateClick, setStartDateClick] = useState(false);

  const [endDate, setEndDate] = useState("날짜 선택");
  const [endDateClick, setEndDateClick] = useState(false);

  const typeOption = [
    { value: "100", label: "IP 조회" },
    { value: "104", label: "계좌번호 변경" },
    { value: "101", label: "공유 정보 변경" },
    { value: "105", label: "닉네임 로그" },
    { value: "106", label: "매칭 로그" },
    { value: "102", label: "전화번호 변경" },
    { value: "103", label: "카드 변경" },
  ];

  const searchTypeOption = [
    typeDropdownValue?.value === "101"
      ? { value: "partyid", label: "PID" }
      : { value: "uid", label: "UID" },
  ];

  useEffect(() => {
    setFilter({
      ...filter,
      type: typeDropdownValue?.value,
      typeLabel: typeDropdownValue?.label,
      searchType: searchType?.value,
      searchValue: searchValue,
    });
  }, [typeDropdownValue, searchType, searchValue]);

  useEffect(() => {
    if (showIpLog.state) {
      setTypeDropdownValue({
        value: "100",
        label: "IP 조회",
      });

      setSearchType({
        value: showIpLog.state ? "uid" : "",
        label: showIpLog.state ? "UID" : "선택",
      });

      setSearchDefaultValue(String(showIpLog.uid));

      setSearchValue(showIpLog.state ? `${showIpLog?.uid}` : "");
    } else {
      setSearchType({
        value: "",
        label: "선택",
      });
      setSearchValue("");
      setSearchDefaultValue(undefined);
    }
  }, [filter?.type, showIpLog]);

  return (
    <UserLogFilterContainer>
      <Flex gapSize={10}>
        <DropDown
          isDisabled={!auth?.userLog || false}
          setDropDownValue={setTypeDropdownValue}
          optionList={typeOption}
          dropdownValue={typeDropdownValue}
          boxWidth={180}
          changeEvent={() => {
            setShowIpLog({
              state: false,
              uid: 0,
            });

            setSearchValue("");
          }}
        />

        <DropDown
          setDropDownValue={setSearchType}
          optionList={searchTypeOption}
          dropdownValue={searchType}
          boxWidth={120}
          isDisabled={typeDropdownValue?.value === ""}
        />

        <Search
          placeholder="입력"
          setSearchValue={setSearchValue}
          isDisabled={searchType?.value === ""}
          defaultValue={searchDefaultValue}
          pageResetEvent={() => setPage(0)}
          inputType="number"
        />
      </Flex>

      <Flex gapSize={10}>
        <ToolTip direction="top" msg="한 번에 최대 7일까지 조회 가능" />

        <LineBoxContainer>
          <CalendarLineBtn
            onClickEvent={() => setStartDateClick(true)}
            value={startDate}
            initValue={"날짜 선택"}
            disabled={typeDropdownValue?.value === ""}
          />

          {startDateClick && (
            <CalendarModal
              closeEvent={() => setStartDateClick(false)}
              changeEvent={(date: string) => setStartDate(date)}
              sPosition="absolute"
              isFutureDisable={true}
            />
          )}
        </LineBoxContainer>

        <CalendarLine />

        <LineBoxContainer>
          <CalendarLineBtn
            onClickEvent={() => setEndDateClick(true)}
            value={endDate}
            initValue={"날짜 선택"}
            disabled={startDate === "날짜 선택"}
          />

          {endDateClick && (
            <CalendarModal
              closeEvent={() => setEndDateClick(false)}
              changeEvent={(date: string) => setEndDate(date)}
              sPosition="absolute"
              dateValue={startDate}
              isAfter={{
                value: 7,
                unit: "day",
              }}
            />
          )}
        </LineBoxContainer>

        <ConfirmBtn
          btnColor="mint"
          btnText="적용"
          btnHeight={38}
          isDisabled={startDate === "날짜 선택" || endDate === "날짜 선택"}
          btnClickEvent={() => {
            setPage(0);
            setFilter({
              ...filter,
              startDate,
              endDate,
            });
          }}
        />

        <LineBtn
          btnColor="mint"
          btnText="초기화"
          icon="refresh"
          btnHeight={38}
          disabled={startDate === "날짜 선택" && endDate === "날짜 선택"}
          btnClickEvent={() => {
            setFilter({
              ...filter,
              startDate: "",
              endDate: "",
            });
            setStartDate("날짜 선택");
            setEndDate("날짜 선택");
          }}
        />
      </Flex>
    </UserLogFilterContainer>
  );
}

const UserLogFilterContainer = styled(PartyInfoFilterContainer)``;

const LineBoxContainer = styled.div`
  position: relative;
`;
